import type { FormId } from "@@/shared/form/form-and-field-id";
import { emptyArrayOf, type DeepPathValueType } from "@towni/common";
import { useMemo, useRef } from "react";
import { type ZodSchema } from "zod";
import { useUpdateEffect } from "../use-update-effect";
import type { Form3ErrorMessage } from "./form3.error-message";
import { useForm3 } from "./form3.use-form";
import { useForm3Field } from "./form3.use-form-field";

/**
 * Custom hook to handle automatic validation of a form field using Zustand and Zod.
 *
 * @template ZSchema - The Zod schema type.
 * @template K - The key of the form data inferred from the Zod schema.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {FormId} [params.formId] - The optional ID of the form.
 * @param {Field} params.fieldId - The name of the field to validate.
 * @param {Function} params.onValidationFinished - Callback function to be called when validation is finished.
 * @param {Object} params.options - Additional options for validation.
 * @param {Function} [params.options.preProcessValue] - Optional function to preprocess the field value before validation.
 * @param {Form3ErrorMessage} [params.options.customFieldValidationErrorMessage] - Optional custom error message for field validation.
 *
 * @returns {Form3ErrorMessage[] | undefined} - An array of error messages or undefined if the form is not available.
 */
const useFormFieldAutoValidation = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
    FieldId extends string,
>(params: {
    readonly formId?: FormId;
    readonly fieldId: FieldId;
    readonly onValidationFinished: (errors: Form3ErrorMessage[]) => void;
    readonly options?: {
        readonly preProcessValue?: (
            value: DeepPathValueType<FormData, FieldId> | undefined | null,
        ) => DeepPathValueType<FormData, FieldId> | undefined;
        readonly customFieldValidationErrorMessage?: Form3ErrorMessage;
    };
}): Form3ErrorMessage[] | undefined => {
    const options = params.options ?? {};
    const fieldId = params.fieldId;
    const onValidationFinished = params.onValidationFinished;
    const validationCountRef = useRef(0);
    const [form] = useForm3({ formId: params.formId });
    const field = useForm3Field<
        FormSchema,
        FormData,
        FieldId,
        DeepPathValueType<FormData, FieldId>
    >({
        formId: form._id,
        fieldId,
    });
    const fieldValue = field?.value;
    const initialFieldValue = field?.value;
    const validate = field?.validate;
    const initialized = useRef(false);
    useUpdateEffect(
        function autoValidate() {
            // If the field value is the same as the initial value
            // and the component has not been initialized yet, return
            // auto validation should not be triggered until after
            // first edit
            if (fieldValue === initialFieldValue && !initialized.current)
                return;
            initialized.current = true;
            let timeout: NodeJS.Timeout;
            const delay = 300;
            const fieldSchema = form?.getFieldSchema(fieldId);
            if (fieldSchema?.safeParseAsync) {
                timeout = setTimeout(() => {
                    validationCountRef.current += 1;
                    const errors = validate?.({
                        ...options,
                        preProcessValue: options.preProcessValue as (
                            value:
                                | DeepPathValueType<FormData, FieldId>
                                | undefined
                                | null,
                        ) => DeepPathValueType<FormData, FieldId> | undefined,
                    });
                    onValidationFinished(
                        errors ?? emptyArrayOf<Form3ErrorMessage>(),
                    );
                }, delay);
            }
            return () => {
                // Clear the timeout when the component unmounts
                // and reset the initialized flag
                if (timeout) clearTimeout(timeout);
                initialized.current = false;
            };
        },
        [fieldValue, form],
    );

    return useMemo(
        () => (!form ? undefined : form.validateField(fieldId)),
        [form, fieldId],
    );
};

export { useFormFieldAutoValidation as useForm3FieldAutoValidation };
